/**
 * src/lib/pdfValidate.ts
 * =============================================================================
 * Validación pre-upload de que un File es REALMENTE un PDF, no un archivo
 * con extensión renombrada (un .xlsx llamado "plano.pdf" pasaba la
 * comprobación de `file.type === 'application/pdf'` que se hace solo
 * con la extensión / mime declarado por el navegador, no por el contenido).
 *
 * Comprobamos el magic number: los PDFs siempre empiezan con los 5 bytes
 * `%PDF-` (0x25 0x50 0x44 0x46 0x2D), seguidos de la versión (`1.4`, `1.7`,
 * `2.0`, etc.). Es trivial de leer en cliente y evita subir basura a R2.
 *
 * Defensa secundaria: el Worker debería también comprobarlo tras el
 * upload (descargar primeros bytes de R2 y validar). Pendiente.
 * =============================================================================
 */

/**
 * Devuelve true si los primeros 5 bytes del archivo son el magic header
 * de un PDF. Lee solo 5 bytes — barato incluso para archivos de cientos
 * de MB.
 */
export async function isValidPdf(file: File): Promise<boolean> {
  // Algunos archivos generados por software antiguo pueden tener BOM o
  // bytes basura al inicio. PDF spec dice que el header DEBE estar en
  // los primeros 1024 bytes — leemos un buffer pequeño para tolerarlo.
  try {
    const head = file.slice(0, 1024)
    const buf = await head.arrayBuffer()
    const bytes = new Uint8Array(buf)
    // Buscar "%PDF-" en los primeros 1024 bytes.
    // 0x25=% 0x50=P 0x44=D 0x46=F 0x2D=-
    for (let i = 0; i <= bytes.length - 5; i++) {
      if (
        bytes[i] === 0x25 &&
        bytes[i + 1] === 0x50 &&
        bytes[i + 2] === 0x44 &&
        bytes[i + 3] === 0x46 &&
        bytes[i + 4] === 0x2d
      ) {
        return true
      }
    }
    return false
  } catch {
    // Si FileReader falla (raro), preferimos rechazar — más seguro que
    // dejar pasar un archivo dudoso.
    return false
  }
}
